import { useEffect, useMemo, useRef, useState } from "react";
import { handleError } from "src/Common/function";
import UserService from "src/Services/API/user.service";
import WOService from "src/Services/API/wo.service";
import CustomerWoTable from "./CustomerWoTable";

function CustomerWO({ customerLocations = [] }) {
  const user = UserService.getCurrentUser();
  const [orderArray, setOrderArray] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [loadMoreButtonShown, setLoadMoreButtonShown] = useState(false);
  const [selectedWorkOrderCustomerId, setSelectedWorkOrderCustomerId] = useState("all");
  const latestRequestRef = useRef(0);
  const normalizedLocations = useMemo(
    () =>
      customerLocations
        .map((customerLocation) => ({
          ...customerLocation,
          id: Number(customerLocation.id),
        }))
        .filter((customerLocation) => customerLocation.id),
    [customerLocations]
  );
  const normalizedCustomerIds = useMemo(
    () =>
      selectedWorkOrderCustomerId === "all"
        ? [...new Set(normalizedLocations.map((customerLocation) => customerLocation.id))]
        : [Number(selectedWorkOrderCustomerId)].filter(Boolean),
    [normalizedLocations, selectedWorkOrderCustomerId]
  );

  useEffect(() => {
    setSelectedWorkOrderCustomerId("all");
  }, [normalizedLocations]);

  useEffect(() => {
    latestRequestRef.current += 1;
    setOrderArray([]);
    setPageNumber(0);
    if (!normalizedCustomerIds.length) {
      setLoadMoreButtonShown(false);
      return;
    }
    getAllWorkOrder([], 0, normalizedCustomerIds);
  }, [normalizedCustomerIds.join(",")]);

  const mergeOrders = (previousOrders, nextOrders) => {
    const uniqueOrders = new Map();

    [...previousOrders, ...nextOrders].forEach((order) => {
      uniqueOrders.set(order.id, order);
    });

    return Array.from(uniqueOrders.values()).sort(
      (firstOrder, secondOrder) =>
        new Date(secondOrder.createdAt || 0).getTime() - new Date(firstOrder.createdAt || 0).getTime()
    );
  };

  const getAllWorkOrder = async (previousOrderArray, pNumber, targetCustomerIds = normalizedCustomerIds) => {
    const requestId = ++latestRequestRef.current;

    try {
      const responses = await Promise.all(
        targetCustomerIds.map((customerId) =>
          WOService.getAllWorkOrders(user.id, "", pNumber, { customer: customerId }, null, 0)
        )
      );

      if (requestId !== latestRequestRef.current) {
        return;
      }

      const nextOrders = responses.flatMap((response) => response.data?.orders || []);
      const totalCount = responses.reduce(
        (count, response) => count + (Number(response.data?.totalCount) || 0),
        0
      );
      const temp = mergeOrders(previousOrderArray, nextOrders);

      setOrderArray(temp);
      setLoadMoreButtonShown(temp.length < totalCount);
    } catch (error) {
      if (requestId === latestRequestRef.current) {
        handleError(error);
      }
    }
  };

  const handleLoadMore = () => {
    if (!normalizedCustomerIds.length) {
      return;
    }
    getAllWorkOrder(orderArray, pageNumber + 1);
    setPageNumber(pageNumber + 1);
  };

  return (
    <div className="w-full bg-backgroundColor">
      <div className="w-full min-h-[30px] flex items-center px-3 py-2 gap-3 flex-wrap">
        <span className="font-OpenSansRegular font-[700] text-[16px] text-titleColor ">Work Orders</span>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setSelectedWorkOrderCustomerId("all")}
            className={`px-3 py-1 rounded-[12px] border text-[12px] font-[500] font-OpenSansRegular ${selectedWorkOrderCustomerId === "all"
              ? "bg-blueColor text-white border-blueColor"
              : "bg-white text-titleColor border-borderColor"
              }`}
          >
            All
          </button>
          {normalizedLocations.map((customerLocation) => (
            <button
              key={customerLocation.id}
              type="button"
              onClick={() => setSelectedWorkOrderCustomerId(customerLocation.id)}
              className={`px-3 py-1 rounded-[12px] border text-[12px] font-[500] font-OpenSansRegular ${String(selectedWorkOrderCustomerId) === String(customerLocation.id)
                ? "bg-blueColor text-white border-blueColor"
                : "bg-white text-titleColor border-borderColor"
                }`}
            >
              {customerLocation.companyName || "Location"}
            </button>
          ))}
        </div>
      </div>
      <CustomerWoTable
        data={orderArray}
        loadMore={handleLoadMore}
        loadMoreButtonShown={loadMoreButtonShown}
        deleteSuccess={null}
        sort={() => { }}
      />
    </div>
  );
}

export default CustomerWO;
